import React, { useRef, useEffect } from 'react';
import SuggestionItem from '../SuggestionsDropdown/SuggestionItem';
import './SuggestionsList.css';

/**
 * Props for SuggestionsList component
 */
interface SuggestionsListProps {
  suggestions: readonly string[];
  selectedIndex: number;
  onSelect: (suggestion: string) => void;
  visible: boolean;
}

/**
 * Dropdown list of suggestions for autocomplete.
 * Handles positioning, scrolling, and selection of suggestions.
 *
 * @param suggestions - List of suggestions to display
 * @param selectedIndex - Currently selected suggestion index
 * @param onSelect - Callback when suggestion is selected
 * @param visible - Whether the dropdown should be shown
 */
const SuggestionsList: React.FC<SuggestionsListProps> = ({
  suggestions,
  selectedIndex,
  onSelect,
  visible
}) => {
  const suggestionsListRef = useRef<HTMLUListElement | null>(null);

  // Position the dropdown near the cursor
  useEffect(() => {
    if (visible && suggestionsListRef.current) {
      const element: HTMLUListElement = suggestionsListRef.current;
      const selection: Selection | null = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range: Range = selection.getRangeAt(0);
        const rect: DOMRect = range.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        // Calculate dropdown width
        const dropdownWidth = element.offsetWidth;

        // Calculate left position, ensuring dropdown stays within viewport
        let leftPosition = rect.left;
        if (leftPosition + dropdownWidth > viewportWidth) {
          leftPosition = viewportWidth - dropdownWidth - 10; // 10px margin from viewport edge
        }

        // Update position to align with cursor while staying in viewport
        element.style.top = `${rect.bottom}px`;
        element.style.left = `${leftPosition}px`;
      }
    }
  }, [visible]);

  // Keep selected item in view when navigating with keyboard
  useEffect(() => {
    if (suggestionsListRef.current) {
      const element: HTMLUListElement = suggestionsListRef.current;
      const selectedElement: HTMLElement = element.children[selectedIndex] as HTMLElement;
      if (selectedElement?.scrollIntoView) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Don't render if not visible
  if (!visible) {
    return null;
  }

  return (
    <ul
      role="listbox"
      ref={suggestionsListRef}
      className="suggestions-list"
      aria-label="suggestions"
    >
      {suggestions.map((suggestion, index) => (
        <SuggestionItem
          key={index}
          suggestion={suggestion}
          index={index}
          isSelected={selectedIndex === index}
          onClick={() => onSelect(suggestion)}
        />
      ))}
    </ul>
  );
};

export default SuggestionsList;